/**
 * AIInterpretationRenderer — универсальный компонент для отображения AI-описаний
 */

import React, { useState, useEffect } from 'react';
import { renderMarkdown } from '../utils/renderMarkdown';
import { sanitizeHtml } from '../utils/sanitizeHtml';

interface AIInterpretationRendererProps {
  aiInterpretation?: string | null;
  title: string;
  icon?: string;
  fallbackMessage?: string;
  isAnalyzing?: boolean;
}

// Динамические сообщения загрузки с иконками
const LOADING_MESSAGES = [
  { icon: '🔮', text: 'Анализируем позиции планет...' },
  { icon: '✨', text: 'Считываем звёздные паттерны...' },
  { icon: '🌙', text: 'Изучаем лунные циклы...' },
  { icon: '💫', text: 'Рассчитываем аспекты совместимости...' },
  { icon: '🌟', text: 'Соединяем энергии двух карт...' },
  { icon: '🔭', text: 'Исследуем кармические связи...' },
  { icon: '💎', text: 'Определяем точки гармонии...' },
  { icon: '🌠', text: 'Формируем интерпретацию...' },
];

const AIInterpretationRenderer: React.FC<AIInterpretationRendererProps> = ({
  aiInterpretation,
  title,
  icon = '✨',
  fallbackMessage = 'Нажмите «Рассчитать» для начала анализа',
  isAnalyzing = false,
}) => {
  const [messageIndex, setMessageIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  useEffect(() => {
    if (!isAnalyzing) return;
    const interval = setInterval(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        setMessageIndex((prev) => (prev + 1) % LOADING_MESSAGES.length);
        setIsTransitioning(false);
      }, 300);
    }, 2500);
    return () => clearInterval(interval);
  }, [isAnalyzing]);

  const cardStyle: React.CSSProperties = {
    background: 'rgba(13, 25, 48, 0.6)',
    backdropFilter: 'blur(20px)',
    border: '1px solid rgba(212, 175, 55, 0.15)',
    borderRadius: '20px',
    padding: '28px',
    marginBottom: '20px',
  };

  if (isAnalyzing) {
    const currentMessage = LOADING_MESSAGES[messageIndex];
    return (
      <div style={{ ...cardStyle, textAlign: 'center', padding: '48px 28px' }}>
        {/* Анимированная иконка */}
        <div style={{
          fontSize: '48px',
          marginBottom: '20px',
          opacity: isTransitioning ? 0 : 1,
          transform: isTransitioning ? 'scale(0.8)' : 'scale(1)',
          transition: 'opacity 0.3s ease, transform 0.3s ease',
          animation: 'pulse-glow 2s ease-in-out infinite',
        }}>
          {currentMessage.icon}
        </div>
        {/* Анимированный текст */}
        <p style={{
          color: '#d4af37',
          fontSize: '16px',
          fontFamily: 'var(--font-display)',
          fontStyle: 'italic',
          margin: 0,
          opacity: isTransitioning ? 0 : 0.9,
          transform: isTransitioning ? 'translateY(8px)' : 'translateY(0)',
          transition: 'opacity 0.3s ease 0.1s, transform 0.3s ease 0.1s',
        }}>
          {currentMessage.text}
        </p>
        {/* Прогресс-бар */}
        <div style={{
          marginTop: '24px',
          height: '3px',
          background: 'rgba(212, 175, 55, 0.1)',
          borderRadius: '2px',
          overflow: 'hidden',
        }}>
          <div style={{
            height: '100%',
            background: 'linear-gradient(90deg, #d4af37, #f4d03f, #d4af37)',
            borderRadius: '2px',
            animation: 'loading-progress 2.5s ease-in-out infinite',
          }} />
        </div>
        <style>{`
          @keyframes pulse-glow {
            0%, 100% { filter: drop-shadow(0 0 8px rgba(212, 175, 55, 0.4)); }
            50% { filter: drop-shadow(0 0 16px rgba(212, 175, 55, 0.7)); }
          }
          @keyframes loading-progress {
            0% { width: 0%; margin-left: 0%; }
            50% { width: 60%; margin-left: 20%; }
            100% { width: 0%; margin-left: 100%; }
          }
        `}</style>
      </div>
    );
  }

  if (!aiInterpretation) {
    return (
      <div style={{ ...cardStyle, textAlign: 'center', padding: '48px' }}>
        <div style={{ fontSize: '36px', marginBottom: '12px', opacity: 0.6 }}>{icon}</div>
        <p style={{ color: '#b0b0b0', margin: 0 }}>{fallbackMessage}</p>
      </div>
    );
  }

  return (
    <div style={cardStyle}>
      <h3 style={{
        color: '#d4af37',
        fontSize: '18px',
        fontFamily: "'Cormorant Garamond', 'Times New Roman', serif",
        fontStyle: 'italic',
        marginBottom: '12px',
        marginTop: 0,
      }}>
        {icon} {title}
      </h3>
      <div
        style={{ color: '#e0e0e0', fontSize: '15px', lineHeight: 1.8 }}
        dangerouslySetInnerHTML={{ __html: sanitizeHtml(renderMarkdown(aiInterpretation)) }}
      />
    </div>
  );
};

export default AIInterpretationRenderer;
